<template>
  <div class="relative w-full h-full">
    <!-- Header -->
    <div class="relative pb-32">
      <div class="px-4 pt-3 pb-3 bg-white rounded-lg shadow-xl overflow-hidden md:px-10 mx-auto w-full">
        <div>
          <h1 class="text-4xl font-bold">
            {{ isEdit ? 'Редактировать объявление' : 'Создать объявление' }}
          </h1>
          <p class="text-blueGray-400 mt-2">{{ isEdit ? 'Изменение данных объявления' : 'Создание нового объявления' }}</p>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="mx-auto w-full -mt-24 mb-12 relative z-index-10">
      <div class="bg-white rounded-lg shadow-xl p-8">
        <form @submit.prevent="saveAnnouncement">
          <!-- Title -->
          <div class="mb-6">
            <label class="block text-blueGray-700 text-sm font-bold mb-2">Название *</label>
            <input
              v-model="form.title"
              type="text"
              required
              class="w-full px-4 py-3 border border-blueGray-300 rounded-lg text-blueGray-600 placeholder-blueGray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Введите название объявления"
            />
          </div>

          <!-- Description -->
          <div class="mb-6">
            <label class="block text-blueGray-700 text-sm font-bold mb-2">Описание *</label>
            <textarea
              v-model="form.description"
              required
              rows="5"
              class="w-full px-4 py-3 border border-blueGray-300 rounded-lg text-blueGray-600 placeholder-blueGray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Введите описание объявления"
            />
          </div>

          <!-- Type -->
          <div class="mb-6">
            <label class="block text-blueGray-700 text-sm font-bold mb-2">Тип объявления *</label>
            <select
              v-model="form.announcement_type"
              required
              class="w-full px-4 py-3 border border-blueGray-300 rounded-lg text-blueGray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="request">Запрос на услугу (от работодателя)</option>
              <option value="offer">Предложение услуги (от пилота)</option>
            </select>
          </div>

          <!-- Services -->
          <div class="mb-6">
            <label class="block text-blueGray-700 text-sm font-bold mb-2">Услуги</label>
            <p class="text-blueGray-500 text-xs mb-3">Выберите требуемые или предлагаемые услуги:</p>
            <div class="space-y-3">
              <div v-for="service in availableServices" :key="service.value" class="flex items-center">
                <input
                  :id="`service-${service.value}`"
                  type="checkbox"
                  :value="service.value"
                  v-model="form.services"
                  class="w-4 h-4 border border-blueGray-300 rounded focus:ring-2 focus:ring-emerald-500 cursor-pointer"
                />
                <label :for="`service-${service.value}`" class="ml-3 text-blueGray-700 text-sm font-medium cursor-pointer">
                  {{ service.label }}
                </label>
              </div>
            </div>
          </div>

          <!-- Budget -->
          <div class="mb-6">
            <label class="block text-blueGray-700 text-sm font-bold mb-2">Бюджет (KZT)</label>
            <input
              v-model="form.budget"
              type="text"
              class="w-full px-4 py-3 border border-blueGray-300 rounded-lg text-blueGray-600 placeholder-blueGray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Например: 50 000 KZT, договорная, 100 000 - 500 000 KZT"
            />
          </div>

          <!-- Location -->
          <div class="mb-6">
            <label class="block text-blueGray-700 text-sm font-bold mb-2">Место проведения</label>
            <input
              v-model="form.location"
              type="text"
              class="w-full px-4 py-3 border border-blueGray-300 rounded-lg text-blueGray-600 placeholder-blueGray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Например: Алматы, Казахстан"
            />
          </div>

          <!-- Contact Info -->
          <div class="mb-6">
            <label class="block text-blueGray-700 text-sm font-bold mb-2">Контактная информация</label>
            <input
              v-model="form.contact_info"
              type="text"
              class="w-full px-4 py-3 border border-blueGray-300 rounded-lg text-blueGray-600 placeholder-blueGray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Email, телефон, и т.д."
            />
          </div>

          <!-- HTML Content Editor -->
          <div class="mb-6">
            <label class="block text-blueGray-700 text-sm font-bold mb-2">Дополнительный HTML контент</label>
            <p class="text-blueGray-500 text-xs mb-3">Редактируйте контент с использованием визуального редактора (опционально)</p>
            <QuillEditor
              :content="form.html_content"
              @update:content="(value) => form.html_content = value"
              content-type="html"
              theme="snow"
              class="bg-white"
              style="border-radius: 0.5rem; overflow: hidden;"
            />
          </div>

          <!-- Status (only for edit) -->
          <div v-if="isEdit" class="mb-6">
            <label class="block text-blueGray-700 text-sm font-bold mb-2">Статус</label>
            <select
              v-model="form.status"
              class="w-full px-4 py-3 border border-blueGray-300 rounded-lg text-blueGray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="pending">На рассмотрении</option>
              <option value="approved">Одобрено</option>
              <option value="rejected">Отклонено</option>
              <option value="archived">Архивировано</option>
            </select>
          </div>

          <!-- Published -->
          <div class="mb-8">
            <label class="flex items-center cursor-pointer">
              <input
                v-model="form.is_published"
                type="checkbox"
                class="w-4 h-4 border border-blueGray-300 rounded focus:ring-2 focus:ring-emerald-500"
              />
              <span class="ml-3 text-blueGray-700 text-sm font-bold">Опубликовано</span>
            </label>
          </div>

          <!-- Buttons -->
          <div class="flex gap-4">
            <button
              type="submit"
              :disabled="loading"
              class="px-8 py-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50 transition font-bold text-sm uppercase"
            >
              {{ loading ? 'Сохранение...' : isEdit ? 'Обновить' : 'Создать' }}
            </button>
            <button
              type="button"
              @click="$router.push('/admin/pilot-announcements')"
              class="px-8 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition font-bold text-sm uppercase"
            >
              Назад
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import { API_PILOT_ANNOUNCEMENTS, API_PILOT_SERVICES_PUBLIC, HEADERS_TOKEN_JSON } from '@/const/uri';

export default {
  name: 'PilotAnnouncementEdit',
  components: {
    QuillEditor,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const form = ref({
      title: '',
      description: '',
      announcement_type: 'request',
      required_pilot_category: '',
      services: [],
      budget: '',
      location: '',
      contact_info: '',
      html_content: '',
      is_published: true,
      status: 'pending'
    });

    const availableServices = ref([]);
    const loading = ref(false);
    const servicesLoading = ref(false);
    const isEdit = computed(() => !!route.params.id);

    const fetchServices = async () => {
      servicesLoading.value = true;
      try {
        const response = await axios.get(API_PILOT_SERVICES_PUBLIC.list, {
          headers: HEADERS_TOKEN_JSON()
        });

        const services = response.data.data || [];
        availableServices.value = services.map(service => ({
          value: service.key,
          label: service.name
        }));
      } catch (error) {
        console.error('Error fetching pilot services:', error);
        availableServices.value = [
        ];
      } finally {
        servicesLoading.value = false;
      }
    };

    const fetchAnnouncement = async () => {
      if (!isEdit.value) return;

      loading.value = true;
      try {
        const response = await axios.get(
          `${API_PILOT_ANNOUNCEMENTS.base}${route.params.id}/`,
          { headers: HEADERS_TOKEN_JSON() }
        );

        if (response.data && response.data.data) {
          const data = response.data.data.data;
          form.value = {
            title: data.title,
            description: data.description,
            announcement_type: data.announcement_type,
            required_pilot_category: data.required_pilot_category || '',
            services: data.services || [],
            budget: data.budget || '',
            location: data.location || '',
            contact_info: data.contact_info || '',
            html_content: data.html_content || '',
            is_published: data.is_published,
            status: data.status
          };
        }
      } catch (error) {
        console.error('Error fetching announcement:', error);
      } finally {
        loading.value = false;
      }
    };

    const saveAnnouncement = async () => {
      loading.value = true;
      try {
        const payload = {
          title: form.value.title,
          description: form.value.description,
          announcement_type: form.value.announcement_type,
          required_pilot_category: form.value.required_pilot_category || null,
          services: form.value.services,
          budget: form.value.budget,
          location: form.value.location,
          contact_info: form.value.contact_info,
          html_content: form.value.html_content || null,
          is_published: form.value.is_published
        };

        if (isEdit.value) {
          payload.status = form.value.status;
        }

        if (isEdit.value) {
          const response = await axios.put(
            `${API_PILOT_ANNOUNCEMENTS.base}${route.params.id}/`,
            payload,
            { headers: HEADERS_TOKEN_JSON() }
          );
          console.log('Update response:', response.data);
        } else {
          const response = await axios.post(
            `${API_PILOT_ANNOUNCEMENTS.create}`,
            payload,
            { headers: HEADERS_TOKEN_JSON() }
          );
          console.log('Create response:', response.data);
        }

        router.push({ name: 'PilotAnnouncementsList' });
      } catch (error) {
        console.error('Error saving announcement:', error);
        console.error('Error response:', error.response?.data);
        alert('Ошибка при сохранении объявления. Пожалуйста, проверьте все поля и попробуйте снова.');
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      fetchServices();
      fetchAnnouncement();
    });

    return {
      form,
      availableServices,
      loading,
      servicesLoading,
      isEdit,
      saveAnnouncement
    };
  }
};
</script>

<style scoped>
</style>
